// This store's alteration tickets, every status, matching an optional search,
// and the actions on them. Online only: tickets are shared by every till in
// the store, and the tailor's progress is only on the server.
// While a search is typed, past bills that match are looked up too, so a
// garment can be taken in against a bill that has no ticket yet.
import { useCallback, useEffect, useRef, useState } from "react";
import { fashionPosService, salesHistoryDetails, salesHistoryService } from "../../../../services";
import {
  ALL_STATUSES,
  LIST_LIMIT,
  buildAlterationStatusMutation,
  buildAlterationsQuery,
  buildBillSearchQuery,
  buildCreateAlterationMutation,
  buildFoundBillLinesQuery,
  mergeAlterations,
  readAlterations,
  readFoundBillLines,
  readFoundBills,
  searchTerm,
} from "../../../../utils/fashion/alterations";
import { readMessage } from "../../../../utils/fashion/holds";

export const SEARCH_DELAY_MS = 300;
// Bill lookups start once the search looks like something a bill can match.
export const MIN_BILL_SEARCH = 3;

export default function useFashionAlterations(tillData, isOnline, search = "") {
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  const online = typeof isOnline === "boolean" ? isOnline : typeof navigator === "undefined" || navigator.onLine;
  const term = searchTerm(search);
  const [state, setState] = useState({ rows: [], status: "idle", bills: [] });
  const latest = useRef(0);

  const reload = useCallback(async () => {
    if (!bunitId || !online) return;
    const call = ++latest.current;
    setState((s) => ({ ...s, status: "loading" }));
    const [lists, bills] = await Promise.all([
      Promise.all(ALL_STATUSES.map((st) => Promise.resolve(fashionPosService(buildAlterationsQuery(bunitId, st, term, LIST_LIMIT))).then(readAlterations, () => null))),
      term.length >= MIN_BILL_SEARCH ? Promise.resolve(salesHistoryService(buildBillSearchQuery(term))).then(readFoundBills, () => null) : Promise.resolve([]),
    ]);
    if (call !== latest.current) return; // a newer search went out
    const failed = lists.some((l) => l === null);
    setState((s) => ({
      rows: failed && lists.every((l) => l === null) ? s.rows : mergeAlterations(lists),
      status: failed ? "error" : "done",
      bills: bills || [],
    }));
  }, [bunitId, online, term]);

  useEffect(() => {
    if (!bunitId || !online) return undefined;
    const timer = setTimeout(reload, term ? SEARCH_DELAY_MS : 0);
    return () => clearTimeout(timer);
  }, [bunitId, online, reload, term]);

  // Items on a found bill, or null when they couldn't be read.
  const billItems = useCallback(async (orderId) => {
    try {
      return readFoundBillLines(await salesHistoryDetails(buildFoundBillLinesQuery(orderId)));
    } catch {
      return null;
    }
  }, []);

  // Bills for the new-alteration form's own search box.
  const findBills = useCallback(async (text) => {
    if (searchTerm(text).length < MIN_BILL_SEARCH) return [];
    try {
      return readFoundBills(await salesHistoryService(buildBillSearchQuery(text)));
    } catch {
      return null;
    }
  }, []);

  const create = useCallback(
    async (alteration) => {
      const res = readMessage(
        await fashionPosService(buildCreateAlterationMutation({ ...alteration, bunitId })),
        "createFashionAlteration",
        "Couldn't save the alteration. Check the connection and try again."
      );
      if (res.ok) reload();
      return res;
    },
    [bunitId, reload]
  );

  const move = useCallback(
    async (alterationId, status) => {
      const res = readMessage(await fashionPosService(buildAlterationStatusMutation(alterationId, status)), "setFashionAlterationStatus", "Couldn't update the alteration. Try again.");
      reload();
      return res;
    },
    [reload]
  );

  return { ...state, online, reload, create, move, billItems, findBills };
}
